'use client';

import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { FileText, Plus } from 'lucide-react';
import { useZadevaStore, type CanvasDocFormat, type CanvasDocMetadata } from '@/lib/stores/zadeva-store';
import { useDocumentViewerStore } from '@/lib/stores/document-viewer-store';
import { TemplateWizard } from './TemplateWizard';

interface CanvasPickerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** HTML content to add as fragment */
  fragmentHtml: string;
  /** Source type for the content block */
  sourceType: 'ai-response' | 'dossier' | 'manual' | 'pasted';
  /** Human-readable source label */
  sourceLabel: string;
}

export function CanvasPickerDialog({
  open,
  onOpenChange,
  fragmentHtml,
  sourceType,
  sourceLabel,
}: CanvasPickerDialogProps) {
  const canvasDocs = useZadevaStore((s) => s.canvasDocs);
  const { createCanvasDoc, addFragmentToCanvas } = useZadevaStore();
  const { setActiveCanvasDocId, setMode, setPendingFragment } = useDocumentViewerStore();
  const togglePanel = useDocumentViewerStore((s) => s.togglePanel);
  const isOpen = useDocumentViewerStore((s) => s.isOpen);

  const [showNewDoc, setShowNewDoc] = useState(false);

  const openCanvasWithFragment = (docId: string) => {
    // Set pending fragment — CanvasEditor will pick it up and insert via TipTap
    setPendingFragment({ html: fragmentHtml, sourceType, sourceLabel });
    setActiveCanvasDocId(docId);
    setMode('canvas');
    if (!isOpen) togglePanel();
    onOpenChange(false);
    setShowNewDoc(false);
  };

  const handleSelectDoc = (docId: string) => {
    openCanvasWithFragment(docId);
  };

  const handleCreateNew = (format: CanvasDocFormat) => {
    const id = createCanvasDoc(format);
    if (!id) return;
    openCanvasWithFragment(id);
  };

  const formatLabels: Record<string, string> = {
    mnenje: 'Mnenje',
    pojasnilo: 'Pojasnilo',
    komentar: 'Komentar',
    analiza: 'Analiza',
    prosto: 'Prosto',
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Dodaj v canvas</DialogTitle>
        </DialogHeader>

        {showNewDoc ? (
          <div className="py-2">
            <TemplateWizard onComplete={handleCreateNew} compact />
          </div>
        ) : (
          <div className="space-y-2 py-2">
            {canvasDocs.length > 0 && (
              <div className="space-y-1.5 max-h-60 overflow-y-auto">
                {canvasDocs
                  .sort((a, b) => b.updatedAt - a.updatedAt)
                  .map((doc) => (
                    <button
                      key={doc.id}
                      onClick={() => handleSelectDoc(doc.id)}
                      className="w-full text-left p-3 rounded-lg border hover:border-primary hover:bg-primary/5 transition-colors flex items-start gap-3"
                    >
                      <FileText className="h-4 w-4 mt-0.5 text-muted-foreground flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-sm truncate">{doc.title}</div>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
                            {formatLabels[doc.format] || doc.format}
                          </Badge>
                          <span className="text-xs text-muted-foreground">
                            {new Date(doc.updatedAt).toLocaleDateString('sl-SI')}
                          </span>
                        </div>
                      </div>
                    </button>
                  ))}
              </div>
            )}

            <Button
              variant="outline"
              className="w-full gap-2 mt-2"
              onClick={() => setShowNewDoc(true)}
            >
              <Plus className="h-4 w-4" />
              Nov canvas dokument
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
